import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Avatar,
  FormControlLabel,
  Switch,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../../components/Context/AuthContext";
import API_URL from "../../config";
import API_BASE_URL from "../../utils/api";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const EditProfile = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    qualification: "",
    profession: "",
    institution: "",
    emailOptOut: false,
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [initialData, setInitialData] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  useEffect(() => {
    if (user) {
      const initial = {
        name: user.name || "",
        email: user.email || "",
        qualification: user.qualification || "",
        profession: user.profession || "",
        institution: user.institution || "",
        emailOptOut: user.emailOptOut || false,
      };
      setFormData(initial);
      setInitialData(initial);
      setImagePreview(user.profileImage || "");
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();

      Object.entries(formData).forEach(([key, value]) =>
        form.append(key, value)
      );

      if (selectedImage) {
        form.append("image", selectedImage);
      }

      const res = await axios.put(`${API_BASE_URL}/auth/update-profile`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Profile updated!");
      setUser(res.data.user);
    } catch (err) {
      console.error(err);
      alert("Failed to update profile.");
    }
  };

  const hasChanges = () => {
    if (!initialData) return false;
    const fieldsChanged = Object.keys(initialData).some(
      (key) => formData[key] !== initialData[key]
    );
    return fieldsChanged || selectedImage !== null;
  };

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" gutterBottom>
        Edit Profile
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Box mt={2}>
          <Typography variant="body1">Profile Image</Typography>
          <input type="file" accept="image/*" onChange={handleImageChange} />
          {imagePreview && (
            <Avatar
              src={imagePreview}
              alt="Preview"
              sx={{ width: 80, height: 80, mt: 2 }}
            />
          )}
        </Box>
        <TextField
          fullWidth
          label="Name"
          name="name"
          margin="normal"
          value={formData.name}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Email"
          name="email"
          margin="normal"
          value={formData.email}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Degree"
          name="qualification"
          margin="normal"
          value={formData.qualification}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Profession"
          name="profession"
          margin="normal"
          value={formData.profession}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Institution"
          name="institution"
          margin="normal"
          value={formData.institution}
          onChange={handleChange}
        />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!formData.emailOptOut}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emailOptOut: !e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label="Subscribe to thread notifications"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.5,
              ml: 4,
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            You'll receive email updates when new threads are posted.
          </Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          disabled={!hasChanges()}
        >
          Save Changes
        </Button>
        <Box
          sx={{
            mt: 3,
            p: 2,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="error"
            fullWidth
            onClick={() => setOpenDeleteDialog(true)}
          >
            Delete My Account
          </Button>
          <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>
            Deleting your account is permanent and cannot be undone.
          </Typography>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Delete Account</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete your account? This action cannot
              be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button
              color="error"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("token");
                  await axios.delete(`${API_BASE_URL}/auth/delete-account`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  alert(
                    "We’re sorry to see you go! Your account has been deleted."
                  );
                  localStorage.removeItem("token");
                  window.location.href = "/"; // Redirect to homepage
                } catch (err) {
                  alert("Failed to delete account. Please try again.");
                }
              }}
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EditProfile;
